import type { DpiaContext, DpiaDetail, DpiaStatus } from 'config/api/dpias/dpias.types';

import {
  buildCompletenessItems,
  mapDetailToFormValues,
  validateDpiaForSubmit,
  type OwnerDpiaFormValues,
  type OwnerDpiaValidationMessages,
} from '../OwnerDpiaEditorPage/ownerDpiaEditor.helpers';

export type AdminDpiaReviewValidationMessages = OwnerDpiaValidationMessages & {
  changedAfterSubmission: string;
  incompleteApproval: string;
  clarificationComment: string;
  highRiskReviewComment: string;
};

export type ReviewSummary = {
  documentedRisks: number;
  highOrVeryHighRisks: number;
  plannedOrOngoingMeasures: number;
  residualRiskLevel: DpiaDetail['residualRiskLevel'];
};

export type ReviewWarning = {
  id: string;
  blocking: boolean;
  message: string;
};

export const isChangedAfterSubmission = (detail: DpiaDetail): boolean =>
  detail.changedAfterSubmission || (detail.submittedRevision !== null && detail.revision !== detail.submittedRevision);

export const canMakeReviewDecision = (status: DpiaStatus): boolean => status === 'ready_for_review';

export const requiresReviewCommentForApproval = (residualRiskLevel: DpiaDetail['residualRiskLevel']): boolean =>
  residualRiskLevel === 'high' || residualRiskLevel === 'very_high';

export const buildReviewSummary = (values: OwnerDpiaFormValues): ReviewSummary => {
  const highOrVeryHighRisks = values.risks.filter(risk => risk.riskLevel === 'high' || risk.riskLevel === 'very_high').length;
  const plannedOrOngoingMeasures = values.risks.reduce(
    (count, risk) => count + risk.measures.filter(measure => measure.status === 'planned' || measure.status === 'in_progress').length,
    0,
  );

  return {
    documentedRisks: values.risks.length,
    highOrVeryHighRisks,
    plannedOrOngoingMeasures,
    residualRiskLevel: values.residualRiskLevel,
  };
};

export const buildBlockingApprovalErrors = (
  detail: DpiaDetail,
  values: OwnerDpiaFormValues,
  messages: AdminDpiaReviewValidationMessages,
): string[] => {
  const errors: string[] = [];

  if (isChangedAfterSubmission(detail)) {
    errors.push(messages.changedAfterSubmission);
  }

  const fieldErrors = validateDpiaForSubmit(values, detail.status, messages);
  if (Object.keys(fieldErrors).length > 0) {
    errors.push(messages.incompleteApproval);
  }

  return errors;
};

export const validateApproveDecision = (
  detail: DpiaDetail,
  values: OwnerDpiaFormValues,
  reviewComment: string,
  messages: AdminDpiaReviewValidationMessages,
): Partial<Record<'reviewComment' | 'approval', string>> => {
  const errors: Partial<Record<'reviewComment' | 'approval', string>> = {};
  const blockingErrors = buildBlockingApprovalErrors(detail, values, messages);

  if (blockingErrors.length > 0) {
    const [firstBlockingError] = blockingErrors;
    errors.approval = firstBlockingError;
  }

  if (requiresReviewCommentForApproval(values.residualRiskLevel) && !reviewComment.trim()) {
    errors.reviewComment = messages.highRiskReviewComment;
  }

  return errors;
};

export const validateClarificationDecision = (
  reviewComment: string,
  messages: Pick<AdminDpiaReviewValidationMessages, 'clarificationComment'>,
): Partial<Record<'reviewComment', string>> => {
  if (!reviewComment.trim()) {
    return { reviewComment: messages.clarificationComment };
  }

  return {};
};

export const buildReviewWarnings = (
  detail: DpiaDetail,
  context: DpiaContext,
  values: OwnerDpiaFormValues,
  messages: {
    changedAfterSubmission: string;
    highResidualRisk: string;
    priorConsultationMayBeRequired: string;
    priorConsultationRequired: string;
    openMeasures: string;
    thirdCountryProcessing: string;
  },
): ReviewWarning[] => {
  const warnings: ReviewWarning[] = [];

  if (isChangedAfterSubmission(detail)) {
    warnings.push({ id: 'changed_after_submission', blocking: true, message: messages.changedAfterSubmission });
  }

  if (requiresReviewCommentForApproval(values.residualRiskLevel)) {
    warnings.push({ id: 'high_residual_risk', blocking: false, message: messages.highResidualRisk });
  }

  if (values.priorConsultationAssessment === 'may_be_required') {
    warnings.push({ id: 'prior_consultation_may_be_required', blocking: false, message: messages.priorConsultationMayBeRequired });
  }

  if (values.priorConsultationAssessment === 'required') {
    warnings.push({ id: 'prior_consultation_required', blocking: false, message: messages.priorConsultationRequired });
  }

  const hasOpenMeasures = values.risks.some(risk =>
    risk.measures.some(measure => measure.status === 'planned' || measure.status === 'in_progress'),
  );
  if (hasOpenMeasures) {
    warnings.push({ id: 'open_measures', blocking: false, message: messages.openMeasures });
  }

  const eeaCountries = new Set([
    'Sweden',
    'Norway',
    'Denmark',
    'Finland',
    'Iceland',
    'Netherlands',
    'Germany',
    'France',
    'Belgium',
    'Spain',
    'Italy',
    'Poland',
    'Austria',
    'Ireland',
    'Portugal',
    'Czech Republic',
    'Hungary',
    'Romania',
    'Bulgaria',
    'Croatia',
    'Slovakia',
    'Slovenia',
    'Estonia',
    'Latvia',
    'Lithuania',
    'Luxembourg',
    'Malta',
    'Cyprus',
    'Greece',
  ]);
  const hasThirdCountry = context.countryHandling.some(country => !eeaCountries.has(country));
  if (hasThirdCountry) {
    warnings.push({ id: 'third_country_processing', blocking: false, message: messages.thirdCountryProcessing });
  }

  return warnings;
};

export const hasClarificationHistory = (detail: DpiaDetail): boolean =>
  Boolean(detail.administratorClarificationComment.trim() || detail.ownerResponseComment.trim());

export const mapDetailToReviewFormValues = mapDetailToFormValues;

export const buildReviewCompletenessItems = (values: OwnerDpiaFormValues, status: DpiaStatus) => buildCompletenessItems(values, status);
